import type { Unit } from "@sins2-atlas/game-data";
import { dataset } from "./dataset";
import type { RosterFilters } from "./roster-filters";
import { VIEW_KINDS, type ViewKind, viewKindOf } from "./stat-model";

// The two views over the Roster (ADR 0008; CONTEXT.md: Ships, Structure, Roster). The Roster
// is the union the dataset carries and stays an internal word; a reader browses it as Ships
// or Structures, the game's own pair ("Build Ships" / "Build Orbital Structures"). Which view
// a unit belongs to is `viewKindOf` in lib/stat-model.ts — the same predicate that decides
// which stat rows the view renders, so the two can never disagree.

type FacetKey = keyof RosterFilters;

export interface RosterView {
  kind: ViewKind;
  path: "/ships" | "/structures";
  /** The nav tab's word — the game's own ("SHIPS" is its input-mapping group). */
  navLabel: string;
  /** The page heading and document title: "Orbital Structures" where there is room, per
   * the glossary ("Unlocks Orbital Structure:", "CIVILIAN ORBITAL STRUCTURES"). */
  heading: string;
  /** The plural for counts and empty states ("12 of 101 ships"). */
  noun: string;
  /** The per-view facets this view offers, in dropdown order. Structures drops Class and
   * Obtained — neither can change its list (all 65 are built from the menu) — and offers
   * Slot type instead, its Class-like facet (CONTEXT.md: Orbit Slot). Race and Faction are
   * NOT here: the Faction Selection is site-wide and lives in the header (ADR 0010). */
  facets: readonly FacetKey[];
  /** The units of this view's kind, in dataset order. */
  units: Unit[];
}

function unitsOf(kind: ViewKind): Unit[] {
  return dataset.units.filter((unit) => viewKindOf(unit) === kind);
}

export const SHIPS_VIEW: RosterView = {
  kind: "ships",
  path: "/ships",
  navLabel: "Ships",
  heading: "Ships",
  noun: "ships",
  facets: ["class", "obtained"],
  units: unitsOf("ships"),
};

export const STRUCTURES_VIEW: RosterView = {
  kind: "structures",
  path: "/structures",
  navLabel: "Structures",
  heading: "Orbital Structures",
  noun: "structures",
  facets: ["slot"],
  units: unitsOf("structures"),
};

/** Both views, in nav order — the same order as VIEW_KINDS. */
export const VIEWS: readonly RosterView[] = VIEW_KINDS.map((kind) =>
  kind === "ships" ? SHIPS_VIEW : STRUCTURES_VIEW,
);

/** The view a unit is listed in — where its entity page's "back" link leads. */
export function viewOf(unit: Pick<Unit, "kind">): RosterView {
  return viewKindOf(unit) === "ships" ? SHIPS_VIEW : STRUCTURES_VIEW;
}
